import { useState } from 'react';
import { ActionIcon, Box, Card, Image, Text } from '@mantine/core';
import { IconDownload, IconTrash } from '@tabler/icons-react';

import { getUTDocumentUrl } from '../../../../../services/upload';

interface AttachmentCardProps {
  id: string;
  fileName: string;
  utFileKey: string;
  isImage: boolean;
  onDelete: (id: string) => void;
  onImageClick?: (utFileKey: string, fileName: string) => void;
}

export function AttachmentCard({
  id,
  fileName,
  utFileKey,
  isImage,
  onDelete,
  onImageClick,
}: AttachmentCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const url = getUTDocumentUrl(utFileKey);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  };

  if (isImage) {
    return (
      <Box
        style={{
          position: 'relative',
          cursor: 'pointer',
          width: '100%',
          aspectRatio: '1',
          overflow: 'hidden',
          borderRadius: '8px',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Image
          src={url}
          alt={fileName}
          h="100%"
          w="100%"
          fit="cover"
          radius="md"
          onClick={() => onImageClick?.(utFileKey, fileName)}
          style={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        />
        <ActionIcon
          color="red"
          size="sm"
          radius="xl"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 200ms ease',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          }}
          onClick={e => {
            e.stopPropagation();
            onDelete(id);
          }}
        >
          <IconTrash size={16} />
        </ActionIcon>
        <Box
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            padding: '8px',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 200ms ease',
          }}
        >
          <Text
            size="xs"
            c="white"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fileName}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <Text
          size="sm"
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fileName}
        </Text>
        <Box style={{ display: 'flex', gap: '8px' }}>
          <ActionIcon
            variant="filled"
            onClick={handleDownload}
            aria-label="Download"
          >
            <IconDownload size={16} />
          </ActionIcon>
          <ActionIcon
            color="red"
            variant="light"
            onClick={() => onDelete(id)}
            aria-label="Delete"
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Box>
      </Box>
    </Card>
  );
}
